import { useState, useCallback, useRef } from 'react';
import { messageService } from '../services/api/chat/messages';
import { APIError } from '../services/api/types';
import type { UserContext } from '../services/api/config';
import type { UIMessage, ChatMessage } from '../services/api/chat/types';

export function useChat(userContext?: UserContext) {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamingTextRef = useRef('');
  const streamingIdRef = useRef('');

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    setIsLoading(true);
    setError(null);

    const userMessage: UIMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Math.floor(Date.now() / 1000),
    };

    // Add user message to UI immediately
    setMessages(prev => [...prev, userMessage]);

    // Build conversation history for the API
    const history: ChatMessage[] = messages.map(msg => ({
      role: msg.role,
      content: msg.content,
    }));
    history.push({ role: 'user', content });

    // Reset streaming refs
    streamingTextRef.current = '';
    streamingIdRef.current = `assistant-${Date.now()}`;

    // Add placeholder assistant message
    var placeholderId = streamingIdRef.current;
    setMessages(prev => [...prev, {
      id: placeholderId,
      role: 'assistant',
      content: '',
      timestamp: Math.floor(Date.now() / 1000),
    }]);

    await messageService.sendStream(history, {
      onStart: function(messageId) {
        streamingIdRef.current = messageId;
        setMessages(prev => prev.map(function(msg) {
          if (msg.id === placeholderId) {
            return { ...msg, id: messageId };
          }
          return msg;
        }));
      },
      onDelta: function(text) {
        streamingTextRef.current += text;
        var currentText = streamingTextRef.current;
        var id = streamingIdRef.current;
        setMessages(prev => prev.map(function(msg) {
          if (msg.id === id || msg.id === placeholderId) {
            return { ...msg, id: id, content: currentText };
          }
          return msg;
        }));
      },
      onComplete: function() {
        setIsLoading(false);
      },
      onError: function(err) {
        // Remove the placeholder message on error
        var id = streamingIdRef.current;
        setMessages(prev => prev.filter(function(msg) {
          return msg.id !== id && msg.id !== placeholderId;
        }));
        setError(err.message);
        setIsLoading(false);
        console.error('Failed to send message:', err);
      },
    }, userContext);
  }, [messages, userContext]);

  return { messages, isLoading, error, sendMessage, clearMessages, setMessages };
}
